import type { CompositionEditorRow } from './compositionEditorAdapter';
import { mapClosingPackageApiError } from './errorMapping';
import type { DocumentSelectionService } from './documentSelectionService';
import type { CompositionRowActionKind } from './compositionRowActions';
import type { AddClosingPackageItemBody } from '../types';

export type CompositionActionExecutionContext = {
  applicationId: number;
  row: CompositionEditorRow;
};

export type CompositionActionHandlerResult = 'success' | 'cancelled' | 'unavailable' | 'error';

export type CompositionActionMutationArgs = {
  applicationId: number;
  itemId: number;
  body?: { comment?: string };
};

export type CompositionActionReplaceArgs = {
  applicationId: number;
  itemId: number;
  body: { documentId: number; comment?: string | null };
};

export type CompositionActionAddItemArgs = {
  applicationId: number;
  body: AddClosingPackageItemBody;
};

export type CompositionDocumentActionTarget = {
  id: number;
  fileType?: string | null;
  fileName?: string;
};

export type CompositionActionHandlerDeps = {
  excludeItem: (args: CompositionActionMutationArgs) => Promise<unknown>;
  restoreItem: (args: CompositionActionMutationArgs) => Promise<unknown>;
  replaceItem: (args: CompositionActionReplaceArgs) => Promise<unknown>;
  addItem: (args: CompositionActionAddItemArgs) => Promise<unknown>;
  previewDocument: (document: CompositionDocumentActionTarget) => Promise<unknown>;
  downloadDocument: (document: CompositionDocumentActionTarget) => Promise<unknown>;
  documentSelectionService: DocumentSelectionService;
  notifySuccess: (message: string) => void;
  notifyError: (error: unknown, fallback: string) => void;
  requestExcludeConfirmation: (
    context: CompositionActionExecutionContext
  ) => Promise<boolean>;
  onRowActionStart?: (rowKey: string) => void;
  onRowActionEnd?: (rowKey: string) => void;
};

export type CompositionActionHandler = (
  context: CompositionActionExecutionContext
) => Promise<CompositionActionHandlerResult>;

/** ActionKind → handler. Wired: EXCLUDE, RESTORE, REPLACE, ADD_DOCUMENT (FE-005A-4…6). */
export type CompositionActionRegistry = Partial<
  Record<CompositionRowActionKind, CompositionActionHandler>
>;

const EXCLUDE_SUCCESS_MESSAGE = 'Позиция исключена из состава комплекта';
const RESTORE_SUCCESS_MESSAGE = 'Позиция восстановлена в составе комплекта';
const REPLACE_SUCCESS_MESSAGE = 'Документ в позиции заменён';
const BIND_SUCCESS_MESSAGE = 'Документ привязан к позиции комплекта';
const MANUAL_ADD_SUCCESS_MESSAGE = 'Документ добавлен в состав комплекта';
const MISSING_ITEM_ID_MESSAGE = 'Позиция состава не найдена — обновите страницу';
const MISSING_DOCUMENT_MESSAGE = 'Документ не привязан к позиции';

export function resolveCompositionDocumentActionTarget(
  row: CompositionEditorRow
): CompositionDocumentActionTarget | null {
  const documents = row.item.documents ?? [];
  const linkedDocument =
    documents.find(
      (document) => Number(document.id) === Number(row.linkedDocumentId)
    ) ?? documents[0];
  const documentId = row.linkedDocumentId ?? linkedDocument?.id;

  if (documentId == null) {
    return null;
  }

  return {
    id: Number(documentId),
    fileType: linkedDocument?.fileType ?? null,
    fileName: linkedDocument?.fileName ?? row.fileName ?? undefined,
  };
}

function requireCompositionItemId(
  row: CompositionEditorRow,
  notifyError: CompositionActionHandlerDeps['notifyError']
): number | null {
  if (row.compositionItemId == null) {
    notifyError(null, MISSING_ITEM_ID_MESSAGE);
    return null;
  }
  return row.compositionItemId;
}

function createExcludeHandler(deps: CompositionActionHandlerDeps): CompositionActionHandler {
  return async (context) => {
    const itemId = requireCompositionItemId(context.row, deps.notifyError);
    if (itemId == null) {
      return 'error';
    }

    const confirmed = await deps.requestExcludeConfirmation(context);
    if (!confirmed) {
      return 'cancelled';
    }

    try {
      await deps.excludeItem({
        applicationId: context.applicationId,
        itemId,
      });
      deps.notifySuccess(EXCLUDE_SUCCESS_MESSAGE);
      return 'success';
    } catch (error) {
      deps.notifyError(
        error,
        mapClosingPackageApiError(error, 'Не удалось исключить позицию из состава')
      );
      return 'error';
    }
  };
}

function createRestoreHandler(deps: CompositionActionHandlerDeps): CompositionActionHandler {
  return async (context) => {
    const itemId = requireCompositionItemId(context.row, deps.notifyError);
    if (itemId == null) {
      return 'error';
    }

    try {
      await deps.restoreItem({
        applicationId: context.applicationId,
        itemId,
      });
      deps.notifySuccess(RESTORE_SUCCESS_MESSAGE);
      return 'success';
    } catch (error) {
      deps.notifyError(
        error,
        mapClosingPackageApiError(error, 'Не удалось восстановить позицию в составе')
      );
      return 'error';
    }
  };
}

function createReplaceHandler(deps: CompositionActionHandlerDeps): CompositionActionHandler {
  return async (context) => {
    const itemId = requireCompositionItemId(context.row, deps.notifyError);
    if (itemId == null) {
      return 'error';
    }

    const selected = await deps.documentSelectionService.requestDocument({
      applicationId: context.applicationId,
      itemKey: context.row.itemKey,
      routeId: context.row.routeId,
      slotLabel: context.row.documentLabel,
      excludeDocumentId: context.row.linkedDocumentId,
      selectionScope: 'slot',
    });

    if (selected == null) {
      return 'cancelled';
    }

    deps.onRowActionStart?.(context.row.rowKey);

    try {
      await deps.replaceItem({
        applicationId: context.applicationId,
        itemId,
        body: { documentId: selected.documentId },
      });
      deps.notifySuccess(REPLACE_SUCCESS_MESSAGE);
      return 'success';
    } catch (error) {
      deps.notifyError(
        error,
        mapClosingPackageApiError(error, 'Не удалось заменить документ в позиции')
      );
      return 'error';
    } finally {
      deps.onRowActionEnd?.(context.row.rowKey);
    }
  };
}

function createAddDocumentHandler(deps: CompositionActionHandlerDeps): CompositionActionHandler {
  return async (context) => {
    const selected = await deps.documentSelectionService.requestDocument({
      applicationId: context.applicationId,
      itemKey: context.row.itemKey,
      routeId: context.row.routeId,
      slotLabel: context.row.documentLabel,
      selectionScope: 'slot',
    });

    if (selected == null) {
      return 'cancelled';
    }

    deps.onRowActionStart?.(context.row.rowKey);

    try {
      if (context.row.compositionItemId != null) {
        await deps.replaceItem({
          applicationId: context.applicationId,
          itemId: context.row.compositionItemId,
          body: { documentId: selected.documentId },
        });
      } else {
        await deps.addItem({
          applicationId: context.applicationId,
          body: {
            documentId: selected.documentId,
            itemKey: context.row.itemKey,
            routeId: context.row.routeId,
          },
        });
      }
      deps.notifySuccess(BIND_SUCCESS_MESSAGE);
      return 'success';
    } catch (error) {
      deps.notifyError(
        error,
        mapClosingPackageApiError(error, 'Не удалось привязать документ к позиции')
      );
      return 'error';
    } finally {
      deps.onRowActionEnd?.(context.row.rowKey);
    }
  };
}

function createDocumentOperationHandler(
  deps: CompositionActionHandlerDeps,
  operation: 'preview' | 'download'
): CompositionActionHandler {
  return async ({ row }) => {
    const document = resolveCompositionDocumentActionTarget(row);
    if (!document) {
      deps.notifyError(null, MISSING_DOCUMENT_MESSAGE);
      return 'error';
    }

    deps.onRowActionStart?.(row.rowKey);

    try {
      if (operation === 'preview') {
        await deps.previewDocument(document);
      } else {
        await deps.downloadDocument(document);
      }
      return 'success';
    } catch (error) {
      deps.notifyError(
        error,
        operation === 'preview'
          ? 'Не удалось открыть документ'
          : 'Не удалось скачать документ'
      );
      return 'error';
    } finally {
      deps.onRowActionEnd?.(row.rowKey);
    }
  };
}

export type ManualAddDocumentDeps = Pick<
  CompositionActionHandlerDeps,
  'addItem' | 'documentSelectionService' | 'notifySuccess' | 'notifyError'
> & {
  onManualAddStart?: () => void;
  onManualAddEnd?: () => void;
};

export type UploadedDocumentRef = {
  id: number;
};

export type UploadedDocumentAddDeps = Pick<
  CompositionActionHandlerDeps,
  'addItem' | 'notifySuccess' | 'notifyError'
> & {
  notifyWarning?: (message: string) => void;
  onUploadAddStart?: () => void;
  onUploadAddEnd?: () => void;
};

/** Header-level manual document add — uses Document Selection Service, not UI. */
export async function executeManualAddDocument(
  deps: ManualAddDocumentDeps,
  applicationId: number,
  disabledDocumentIds: number[] = []
): Promise<CompositionActionHandlerResult> {
  const selected = await deps.documentSelectionService.requestDocument({
    applicationId,
    itemKey: '',
    routeId: null,
    slotLabel: 'Добавление документа вручную',
    selectionScope: 'manual',
    disabledDocumentIds,
  });

  if (selected == null) {
    return 'cancelled';
  }

  deps.onManualAddStart?.();

  try {
    await deps.addItem({
      applicationId,
      body: { documentId: selected.documentId },
    });
    deps.notifySuccess(MANUAL_ADD_SUCCESS_MESSAGE);
    return 'success';
  } catch (error) {
    deps.notifyError(
      error,
      mapClosingPackageApiError(error, 'Не удалось добавить документ в состав')
    );
    return 'error';
  } finally {
    deps.onManualAddEnd?.();
  }
}

/**
 * Toolbar upload continuation: binds the first newly created registry Document
 * as a manual composition item. Upload itself remains owned by UploadDocumentModal.
 */
export async function executeUploadedDocumentAdd(
  deps: UploadedDocumentAddDeps,
  applicationId: number,
  documents: UploadedDocumentRef[]
): Promise<CompositionActionHandlerResult> {
  const uploaded = documents[0];
  if (!uploaded) {
    deps.notifyError(null, 'Загрузка завершена без созданного документа');
    return 'error';
  }

  deps.onUploadAddStart?.();

  try {
    await deps.addItem({
      applicationId,
      body: { documentId: uploaded.id },
    });
    deps.notifySuccess('Документ загружен и добавлен в состав комплекта');
    if (documents.length > 1) {
      deps.notifyWarning?.(
        'В состав добавлен первый загруженный документ; остальные доступны в реестре'
      );
    }
    return 'success';
  } catch (error) {
    deps.notifyError(
      error,
      mapClosingPackageApiError(error, 'Документ загружен, но не добавлен в состав')
    );
    return 'error';
  } finally {
    deps.onUploadAddEnd?.();
  }
}

/** Builds the Action Registry — sole layer that orchestrates RTK mutations (via injected deps). */
export function createCompositionActionRegistry(
  deps: CompositionActionHandlerDeps
): CompositionActionRegistry {
  return {
    EXCLUDE: createExcludeHandler(deps),
    RESTORE: createRestoreHandler(deps),
    REPLACE: createReplaceHandler(deps),
    ADD_DOCUMENT: createAddDocumentHandler(deps),
    PREVIEW: createDocumentOperationHandler(deps, 'preview'),
    DOWNLOAD: createDocumentOperationHandler(deps, 'download'),
  };
}

/** Resolves handler by ActionKind and executes. Returns `unavailable` when action is not wired. */
export async function executeCompositionRowAction(
  registry: CompositionActionRegistry,
  kind: CompositionRowActionKind,
  context: CompositionActionExecutionContext
): Promise<CompositionActionHandlerResult> {
  const handler = registry[kind];
  if (!handler) {
    return 'unavailable';
  }
  return handler(context);
}
